/**
 * Purpose: Manages expand/collapse for long message content
 *
 * How it works:
 * 1. Checks if the message text exceeds a character threshold
 * 2. Applies a CSS max-height constraint; clicking "Show more" reveals the full message at once
 * 3. Compares scrollHeight vs clientHeight via a ref to detect if content is still clipped
 * 4. Automatically hides the button when content fits
 *
 * Key features:
 * - Pure CSS clipping — never mutates the HTML or TipTap document
 * - Single-step reveal: "Show more" expands to full content, "Show less" collapses
 * - Works with any rich-text formatting, emojis, or embedded elements
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

/** Messages with plain text shorter than this are never truncated */
export const EXPANDABLE_CHAR_THRESHOLD = 3000;

/** Initial visible height in pixels (~12-15 lines of text) */
export const INITIAL_MAX_HEIGHT = 700;

/** Height of the gradient fade overlay at the bottom of truncated content */
export const GRADIENT_HEIGHT = 48;

/** Data attribute placed on the message list scroll container */
export const SCROLL_CONTAINER_ATTR = "data-message-scroll-container";

/**
 * Walks up the DOM tree to find the message list scroll container.
 * Looks for the SCROLL_CONTAINER_ATTR first; falls back to the nearest
 * scrollable ancestor if the attribute is not found.
 */
function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    if (parent.hasAttribute(SCROLL_CONTAINER_ATTR)) {
      return parent;
    }
    parent = parent.parentElement;
  }

  // Fallback: nearest scrollable ancestor
  parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export function useExpandableMessage(textLength: number) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(
    textLength > EXPANDABLE_CHAR_THRESHOLD,
  );

  const isCollapsible = textLength > EXPANDABLE_CHAR_THRESHOLD;

  // When collapsed, cap at initial height; when expanded, remove the cap entirely
  const computedMaxHeight =
    isCollapsible && !isExpanded ? INITIAL_MAX_HEIGHT : undefined;

  // Check whether the content overflows the current max-height
  const checkTruncation = useCallback(() => {
    const el = contentRef.current;
    if (!el || !isCollapsible || isExpanded) {
      setIsTruncated(false);
      return;
    }
    // Content is truncated when its natural height exceeds the computed cap
    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [isCollapsible, isExpanded]);

  // Re-check truncation when expansion state or checkTruncation changes
  useEffect(() => {
    checkTruncation();
  }, [isExpanded, checkTruncation]);

  // Observe resize so dynamic content (images, lazy-loaded embeds) is handled
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !isCollapsible) {
      return;
    }

    let mounted = true;
    const observer = new ResizeObserver(() => {
      if (mounted) {
        checkTruncation();
      }
    });
    observer.observe(el);

    return () => {
      mounted = false;
      observer.disconnect();
    };
  }, [isCollapsible, checkTruncation]);

  /**
   * Applies a state update while keeping the content element visually pinned.
   * Uses flushSync so the entire measure → commit → re-measure → scroll-adjust
   * cycle runs synchronously before the browser paints.
   */
  const updateWithScrollLock = useCallback((expanded: boolean) => {
    const el = contentRef.current;
    if (!el) {
      setIsExpanded(expanded);
      return;
    }

    const container = findScrollContainer(el);
    if (!container) {
      setIsExpanded(expanded);
      return;
    }

    // 1. Snapshot the element's viewport position before the DOM change
    const topBefore = el.getBoundingClientRect().top;

    // 2. Commit the state change synchronously so the DOM updates immediately
    flushSync(() => {
      setIsExpanded(expanded);
    });

    // 3. Measure the new position and compensate scroll
    const topAfter = el.getBoundingClientRect().top;
    const drift = topAfter - topBefore;
    if (drift !== 0) {
      container.scrollTop += drift;
    }
  }, []);

  const expand = useCallback(() => {
    updateWithScrollLock(true);
  }, [updateWithScrollLock]);

  const collapse = useCallback(() => {
    updateWithScrollLock(false);
  }, [updateWithScrollLock]);

  return {
    contentRef,
    /** Pixel max-height to apply, or undefined when no capping is needed */
    maxHeight: computedMaxHeight,
    /** True when the message exceeds the character threshold */
    isCollapsible,
    /** True when content is currently clipped by max-height */
    isTruncated,
    /** True when the user has fully expanded the message */
    isFullyExpanded: isCollapsible && isExpanded,
    /** Expand to show the full message */
    expand,
    /** Collapse back to the initial height */
    collapse,
  };
}
